import { SHEET_KEYS } from '../../../models/sheet-keys';
import {
  PureSheetValidationResult,
  SheetValidationContext,
  SheetValidator,
} from '../sheet-validator';
import {
  pickRole21Schema,
  Role21Schema,
  Role21SchemaControl,
} from './role21-notes-accounting-policies.rules';

/**
 * Pure validator for Role 21 (Notes - Summary of Material Accounting
 * Policies).
 *
 * Mirrors the editor's role21-720000-notes-accounting-policies.component.ts
 * validation surface:
 *
 *  - Static required fields from schema (control.required === true OR
 *    validators[{ name: 'required' }]) — see the component's getValidators()
 *    which pushes Validators.required when control.required is set. We also
 *    honour the validators-array form so a future schema revision that
 *    switches to the {name:'required'} style (as several newer roles do)
 *    does not silently drop the check. Mirrors the role 6 latent-bug-fix
 *    pattern.
 *  - No cross-sheet rules: the role 21 schemas do not declare any
 *    requiredIfCrossSheet entries, and the component's
 *    setupCrossReferenceAutoPopulation is an auto-fill convenience (it
 *    writes values, it never adds validators), so nothing to reproduce
 *    here.
 *  - No conditional-on-same-sheet required rules: the schemas have no
 *    validators entries with conditionalOn, and shouldDisplayField only
 *    affects visibility (hidden fields are not cleared, and there are no
 *    hidden required fields today).
 *
 * ---- Schema variant handling ----------------------------------------------
 *
 * Role 21 has TWO form JSONs (role-720000-mfrs.form.json and
 * role-720000-mpers.form.json) that differ in qname prefix and MFRS/MPERS
 * label copy. The component fetches the right one at mount time based on
 * filingPrerequisites['ssmt-dei:BasisOfAccountingStandardsAppliedToPrepareFinancialStatements'].
 *
 * The pure path gets the same value through context.accountingStandard
 * (assembled by ConversionValidatorService.buildContext from filingInfo —
 * which is the same DEI field). Selection is delegated to pickRole21Schema
 * so the component and validator stay DRY.
 */

interface SchemaWithControls {
  controls: ReadonlyArray<Role21SchemaControl>;
}

function isEmpty(value: unknown): boolean {
  return value === null || value === undefined || value === '';
}

/**
 * Mirrors the component's getValidators() check: a control is statically
 * required when `required === true`. We additionally accept the
 * validators-array form (`validators: [{ name: 'required' }]`) so a future
 * schema migration to that style does not silently drop the check — same
 * latent-bug-guard pattern applied in role 6's validator.
 */
function isStaticallyRequired(control: Role21SchemaControl): boolean {
  if (control.required === true) return true;
  return Array.isArray(control.validators)
    ? control.validators.some((validator) => validator?.name === 'required')
    : false;
}

function checkStaticRequiredErrors(
  schema: SchemaWithControls,
  sheetData: Record<string, any>
): Record<string, string> {
  const errors: Record<string, string> = {};
  for (const control of schema.controls) {
    if (isStaticallyRequired(control) && isEmpty(sheetData[control.qname])) {
      errors[control.qname] = `${control.qname} is required`;
    }
  }
  return errors;
}

export const role21NotesAccountingPoliciesValidator: SheetValidator = {
  sheetKey: SHEET_KEYS.notesAccountingPolicies,
  // Tier 2: the validation logic itself is required-field-only (Tier 1 style)
  // but schema selection depends on the filing's accounting standard — a
  // read of filingPrerequisites exposed through context.accountingStandard.
  // Keeping MFRS/MPERS-variant sheets at Tier 2 means the parity harness
  // surfaces any selection-mismatch as an explicit validator-level
  // discrepancy rather than a mysterious required-field drift.
  tier: 2,
  validate(
    sheetData: Record<string, any>,
    context: SheetValidationContext
  ): PureSheetValidationResult {
    const schema = pickRole21Schema(
      context.accountingStandard
    ) as unknown as SchemaWithControls & Role21Schema;

    const errors: Record<string, string> = {
      ...checkStaticRequiredErrors(schema, sheetData),
    };

    return {
      hasErrors: Object.keys(errors).length > 0,
      hasSuggestedChanges: false,
      errors,
    };
  },
};
